import { IAction } from 'helpers/interfaces';
import { sort, dataFormatterUtils } from 'utils';
import { PlayerReportTypes } from 'redux/actions/backoffice/player-report-actions';
import dayjs from 'dayjs';

export interface IPlayerData {
    gameId: number;
    startId: number;
    gameSessionId: number;
    totalBet: number;
    totalWin: number;
    net?: number;
    balance: number;
    playerId: number;
    startBalance?: number;
    endBalance?: number;
    currencyId?: number;
    callback?: Function;
    roundId: string;
    gameType: string;
}

export interface IPromotionData {
    bet: number;
    expire: Date;
    gameId: number;
    grantedFreeRounds: number;
    promotionId: string;
    remainedFreeRounds: number;
    win: number;
}

export interface IPlayerFilters {
    roundId: string | null;
    fromDate: dayjs.Dayjs | null;
    toDate: dayjs.Dayjs | null;
    roundTypes: string[];
    sortBy: string;
    sortOrder: 'ASCENDING' | 'DESCENDING';
    pageSize: number;
    currentPage: number;
}

export interface IPlayer {
    playerData: IPlayerData[];
    sortedPlayerData: IPlayerData[];
    playerInternalId: string | null;
    currencyId?: number | null;
    promotionData?: IPromotionData;
    campaignsData?: any;
    playerDoesntExist: boolean;
    pagination?: {
        total: number;
        currentPage: number;
        totalPages: number;
    } | null;
    filters: IPlayerFilters;
    isLoading: boolean;
    expectedPlayerId: string | null;
}

const getDefaultDateRange = () => {
    const today = dayjs();
    return { fromDate: today.startOf('day'), toDate: today.endOf('day') };
};

const createDefaultFilters = (): IPlayerFilters => {
    const { fromDate, toDate } = getDefaultDateRange();
    return {
        roundId: null,
        fromDate,
        toDate,
        roundTypes: ['regular', 'buy', 'free-round', 'awarded-feature'],
        sortBy: 'startId',
        sortOrder: 'DESCENDING',
        pageSize: 20,
        currentPage: 1,
    };
};

export const playerInitialState: IPlayer = {
    playerData: [],
    sortedPlayerData: [],
    currencyId: null,
    campaignsData: null,
    playerInternalId: null,
    playerDoesntExist: false,
    pagination: null,
    filters: createDefaultFilters(),
    isLoading: false,
    expectedPlayerId: null,
};

const playerReportReducer = (state = playerInitialState, actions: IAction) => {
    switch (actions.type) {
        case PlayerReportTypes.PLAYER_REPORT_REQUEST:
        case PlayerReportTypes.OLD_PLAYER_REPORT_REQUEST: {
            const { playerId } = actions.payload || {};
            return {
                ...state,
                isLoading: true,
                playerDoesntExist: false,
                expectedPlayerId: playerId ? String(playerId) : null,
            };
        }
        
        case PlayerReportTypes.PLAYER_CAMPAIGNS_DATA_REQUEST: {
            return {
                ...state,
                campaignsData: null,
            };
        }

        case PlayerReportTypes.PLAYER_REPORT_SUCCESS:
        case PlayerReportTypes.OLD_PLAYER_REPORT_SUCCESS: {
            const { content, extraInfo } = actions.payload;
            const { playerCurrencyId, internalPlayerId, pagination } = extraInfo;
            
            const responsePlayerId = internalPlayerId ? String(internalPlayerId) : 
                (content && content.length > 0 && content[0].playerId ? String(content[0].playerId) : null);
            
            if (state.expectedPlayerId && responsePlayerId && state.expectedPlayerId !== responsePlayerId) {
                return state;
            }
            
            const supplementedContent = content.map((item: IPlayerData) => {
                return { ...item, currencyId: playerCurrencyId };
            });

            return {
                ...state,
                playerData: supplementedContent,
                currencyId: playerCurrencyId,
                pagination: pagination,
                playerInternalId: internalPlayerId,
                sortedPlayerData: [],
                playerDoesntExist: false,
                isLoading: false,
                expectedPlayerId: responsePlayerId || state.expectedPlayerId,
            };
        }

        case PlayerReportTypes.PLAYER_REPORT_FAILED:
        case PlayerReportTypes.OLD_PLAYER_REPORT_FAILED: {
            const { code } = actions.payload.response.data.error;

            return { 
                ...state, 
                playerDoesntExist: code === 5,
                isLoading: false,
            };
        }

        case PlayerReportTypes.PLAYER_REPORT_SORT: {
            const { sortState } = actions.payload;

            return {
                ...state,
                sortedPlayerData: sortState
                    ? sort(
                          actions.payload,
                          dataFormatterUtils.getFormattedData(state.playerData, false),
                      )
                    : [],
            };
        }

        case PlayerReportTypes.PROMOTION_DATA_SUCCESS: {
            return {
                ...state,
                promotionData: actions.payload,
            };
        }

        case PlayerReportTypes.PLAYER_CAMPAIGNS_DATA_SUCCESS: {
            return {
                ...state,
                campaignsData: actions.payload,
            };
        }

        case PlayerReportTypes.SET_ROUND_ID: {
            const { roundId } = actions.payload;
            const nextRoundId = roundId || null;
            const hasRoundId = nextRoundId && nextRoundId.trim().length > 0;
            const nextDateRange = hasRoundId
                ? { fromDate: state.filters.fromDate, toDate: state.filters.toDate }
                : (state.filters.fromDate && state.filters.toDate
                    ? { fromDate: state.filters.fromDate, toDate: state.filters.toDate }
                    : getDefaultDateRange());
            return {
                ...state,
                filters: {
                    ...state.filters,
                    roundId: nextRoundId,
                    fromDate: nextDateRange.fromDate,
                    toDate: nextDateRange.toDate,
                    currentPage: 1,
                },
            };
        }

        case PlayerReportTypes.SET_DATE_RANGE: {
            const { fromDate, toDate } = actions.payload;
            const hasRoundId = state.filters.roundId && state.filters.roundId.trim().length > 0;
            const hasCompleteRange = !!(fromDate && toDate);
            const fallbackRange = hasCompleteRange
                ? { fromDate, toDate }
                : (hasRoundId ? { fromDate: null, toDate: null } : getDefaultDateRange());
            return {
                ...state,
                filters: {
                    ...state.filters,
                    fromDate: fallbackRange.fromDate,
                    toDate: fallbackRange.toDate,
                    roundId: hasCompleteRange ? null : state.filters.roundId,
                    currentPage: 1,
                },
            };
        }

        case PlayerReportTypes.SET_ROUND_TYPES: {
            const { roundTypes } = actions.payload;
            return {
                ...state,
                filters: {
                    ...state.filters,
                    roundTypes: roundTypes.length > 0 ? roundTypes : state.filters.roundTypes,
                    currentPage: 1,
                },
            };
        }

        case PlayerReportTypes.SET_SORT: {
            const { sortBy, sortOrder } = actions.payload;
            return {
                ...state,
                filters: {
                    ...state.filters,
                    sortBy: sortBy || state.filters.sortBy,
                    sortOrder: sortOrder || state.filters.sortOrder,
                },
            };
        }

        case PlayerReportTypes.SET_PAGINATION: {
            const { currentPage, pageSize } = actions.payload;
            return {
                ...state,
                filters: {
                    ...state.filters,
                    currentPage: currentPage !== undefined ? currentPage : state.filters.currentPage,
                    pageSize: pageSize !== undefined ? pageSize : state.filters.pageSize,
                },
            };
        }

        case PlayerReportTypes.CLEAR_ROUND_ID: {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    roundId: null,
                },
            };
        }

        case PlayerReportTypes.CLEAR_DATE_FILTER: {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    fromDate: null,
                    toDate: null,
                },
            };
        }

        case PlayerReportTypes.RESET_FILTERS: {
            return {
                ...state,
                filters: createDefaultFilters(),
            };
        }

        case PlayerReportTypes.INIT_FILTERS_FROM_URL: {
            const { filters: urlFilters } = actions.payload;
            const mergedFilters = {
                ...state.filters,
                ...urlFilters,
            };
            const hasRoundId = mergedFilters.roundId && mergedFilters.roundId.trim().length > 0;
            const defaultRange = getDefaultDateRange();
            return {
                ...state,
                filters: {
                    ...mergedFilters,
                    fromDate: hasRoundId ? null : (mergedFilters.fromDate || defaultRange.fromDate),
                    toDate: hasRoundId ? null : (mergedFilters.toDate || defaultRange.toDate),
                    roundId: hasRoundId ? mergedFilters.roundId : null,
                },
            };
        }

        case PlayerReportTypes.CLEAR_PLAYER_DATA: {
            return {
                ...state,
                playerData: [],
                sortedPlayerData: [],
                playerInternalId: null,
                currencyId: null,
                pagination: null,
                playerDoesntExist: false,
                campaignsData: null,
                expectedPlayerId: null,
            };
        }

        default:
            return state;
    }
};

export default playerReportReducer;
